/**
 * Колесо над горизонтальным рядом (полки карточек, рельса разделов): щелчок колеса мыши листает ряд вбок,
 * жест трекпада или Magic Mouse уходит странице нетронутым.
 *
 * Зачем. Полки листались колесом «в сторону» ради Windows-мыши без горизонтальной прокрутки (владелец: «Инструменты»
 * на главной не листались колесиком). Признаком трекпада служил ненулевой deltaX, но прямое движение двумя пальцами
 * даёт deltaX = 0, и такие события полка забирала себе: страница стояла, полка ехала вбок, а когда палец чуть уходил
 * в сторону, события проходили, и страница ехала дальше. Это чередование и было «подтормаживанием» над лентой
 * (владелец 30.09). Замер: 5 щелчков над лентой — страница 0 px, полка 448 px; над текстом — страница 500 px.
 *
 * Как отличаем щелчок от жеста. Firefox отдаёт мышь строками (deltaMode ≠ 0), трекпад — пикселями; deltaMode читаем
 * первым: Firefox показывает строки только тому, кто спросил режим до величины, иначе притворяется пикселями. Chrome и
 * Safari держат legacy-поле wheelDeltaY: у щелчка мыши оно кратно 120, у жеста всегда равно −3·deltaY. Всё, что не
 * щелчок, — жест. Мышь на Mac и мыши с плавным колесом без щелчков попадают в жест: у них остаются кнопки и протяжка.
 * Непрерывный жест (события чаще 60 мс) остаётся жестом: одиночный шаг трекпада, по величине совпавший со щелчком
 * (Windows-тачпад иногда шлёт ровно 120), не перехватит страницу посреди прокрутки.
 *
 * Слушатель не пассивный: щелчку нужен preventDefault, иначе страница поехала бы вместе с рядом. Жест выходит из
 * обработчика первой же проверкой.
 */
type LegacyWheel = WheelEvent & { wheelDeltaY?: number }

/** Щелчок колеса мыши (дискретный шаг), а не жест трекпада или Magic Mouse. */
export function isWheelNotch(e: WheelEvent): boolean {
	const lines = e.deltaMode !== 0
	if (e.deltaX !== 0 || e.deltaY === 0) return false
	if (lines) return true
	const wd = (e as LegacyWheel).wheelDeltaY
	if (typeof wd !== 'number' || wd === 0) return false
	return wd % 120 === 0 && wd !== e.deltaY * -3
}

/** Пикселей на строку для щелчка «строками»: обычный щелчок — 3 строки, как шаг Chrome в 100 px. */
export const LINE_PX = 33

/** Шаг щелчка в пикселях ряда: строки и страницы переводим, пиксели берём как есть. */
export function notchPixels(e: WheelEvent, el: HTMLElement): number {
	if (e.deltaMode === 1) return e.deltaY * LINE_PX
	if (e.deltaMode === 2) return e.deltaY * el.clientWidth
	return e.deltaY
}

/** Ближе этого между событиями — тот же жест. */
export const GESTURE_GAP_MS = 60

/** Запас меньше этого — ряду ехать некуда: дробные ширины дают 1–2 px «переполнения», и щелчок над рельсой разделов
 * съедался впустую, страница на него не двигалась. */
export const EDGE_PX = 2

/** Вешает колесо на ряд: щелчки мыши едут по X, пока есть куда, остальное — странице. Возвращает снятие. */
export function attachRowWheel(el: HTMLElement): () => void {
	let lastAt = -Infinity
	let lastGesture = false
	const onWheel = (e: WheelEvent) => {
		const gesture = (lastGesture && e.timeStamp - lastAt < GESTURE_GAP_MS) || !isWheelNotch(e)
		lastAt = e.timeStamp
		lastGesture = gesture
		if (gesture) return
		// Перехватываем, ТОЛЬКО пока есть куда ехать: на краю ряда страница едет дальше.
		const max = el.scrollWidth - el.clientWidth
		if (max <= EDGE_PX) return
		if ((e.deltaY < 0 && el.scrollLeft <= EDGE_PX) || (e.deltaY > 0 && el.scrollLeft >= max - EDGE_PX)) return
		e.preventDefault()
		el.scrollLeft += notchPixels(e, el)
	}
	el.addEventListener('wheel', onWheel, { passive: false })
	return () => el.removeEventListener('wheel', onWheel)
}
